import React, { FC } from 'react';
import { StorageData, useStorage } from '~Components/Storage';
import SelectList from '~ComponentsImproved/SelectList';
import Toggle from '~ComponentsImproved/Toggle';
import { useDispatch, useI18n, useLogger, useMetadata, useSelector } from '~Hooks';
import { SettingsBlock } from '~Scenes/Settings-v2';
import { Box } from '~Styles';
import { addToast } from '~actions';
import { HIDE_IN, TogglesConfig, isHiddenOnDevice } from '~Scenes/Settings-v2/types';

interface Props {
  /** Set by the Handy shell; the tablet route renders without it */
  isHandy?: boolean;
}

const TablesSettings: FC<Props> = ({ isHandy = false }) => {
  const { i18n } = useI18n();
  const [storage, commitToStorage, storageLoading] = useStorage();
  const logger = useLogger('settings-v2-tables');
  const meta = useMetadata();
  const zones = useSelector((state) => state.zones ?? []);
  const dispatch = useDispatch();

  function updateStorage(data: Partial<StorageData>) {
    logger.info('Updating Settings (Tables Settings)', { fields: data });
    commitToStorage(data);
  }

  const updateZone = (id: string) => {
    logger.info('Updating Settings (change zone)', {
      fields: { selectedZoneId: id },
    });
    updateStorage({ zoneId: id === 'NONE' ? null : id });
    dispatch(addToast(i18n('TOAST.Zone.Updated'), true));
  };

  const togglesConfig: Array<TogglesConfig> = [
    {
      label: i18n('Settings.HideCoverageModal.Title'),
      subLabel: i18n('Settings.HideCoverageModal.Description'),
      key: 'hideCoverageModal',
      storage: storage.hideCoverageModal,
    },
    {
      label: i18n('Settings.Tables.ShowSeatingTime'),
      key: 'showSeatingTimeInTSR',
      storage: storage.showSeatingTimeInTSR,
    },
    {
      label: i18n('Settings.Tables.ShowOrderStatusInTSR'),
      subLabel: i18n('Settings.Tables.ShowOrderStatusInTSR.Text'),
      key: 'KDSShowOrderStatusInTSR',
      storage: storage.KDSShowOrderStatusInTSR,
    },
    // On tablets this lives in the KDS scene, which handhelds don't have. Only
    // relevant when KDS creates one order per course, otherwise there is no
    // first course to hold.
    {
      label: i18n('Settings.KDS.HoldFirstCourse'),
      subLabel: i18n('Settings.KDS.HoldFirstCourse.Text'),
      key: 'holdFirstCourse',
      storage: storage.holdFirstCourse,
      hideIn: !meta.data.useCoursesInKDS ? HIDE_IN.BOTH : HIDE_IN.TABLET,
    },
  ];

  if (storageLoading) {
    return null;
  }

  return (
    <Box
      padding={isHandy ? 'none' : 'large'}
      marginTop={isHandy ? 'small' : 'none'}
      display="flex"
      flexDirection="column"
      gap={isHandy ? 'small' : 'large'}>
      <SettingsBlock compact={isHandy}>
        <SelectList
          compact={isHandy}
          label={i18n('Settings.Zone')}
          list={[
            {
              id: 'NONE',
              name: i18n('Settings.ZoneNone') + ' ' + i18n('Settings.ZoneDontAssign'),
            },
            ...zones.map((zone) => ({
              id: zone.id,
              name: zone.name,
            })),
          ]}
          selectedId={storage.zoneId ?? 'NONE'}
          onSelect={updateZone}
        />
        {togglesConfig.map(
          (toggle) =>
            !isHiddenOnDevice(toggle.hideIn, isHandy) && (
              <Toggle
                key={toggle.key}
                settingKey={toggle.key}
                label={`${toggle.label} ${storage.devMode ? '[' + toggle.key + ']' : ''} `}
                description={toggle.subLabel}
                initialValue={toggle.inverted ? !toggle.storage : toggle.storage}
                onToggle={() => updateStorage({ [toggle.key]: !toggle.storage })}
                fullWidth
                darkBackground
                lightText
                compact={isHandy}
              />
            ),
        )}
      </SettingsBlock>
    </Box>
  );
};

export default TablesSettings;
